import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { TOOLBAR_COLORS } from "components/toolbars/constants";
import ResetIcon from "media/icons/reset.svg?react";

import { FilterToken } from "./FilterToken";
import type { ModificationCategory } from "./useViewModificationCategories";

interface ViewModificationsFlyoutProps {
    categories: ModificationCategory[];
    onResetAll: () => void;
}

export function ViewModificationsFlyout({ categories, onResetAll }: ViewModificationsFlyoutProps) {
    const { t } = useTranslation();
    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                // Top-right corner is flush against the toolbar — no radius there
                borderRadius: "8px 0 8px 8px",
                backgroundColor: TOOLBAR_COLORS.WHITE,
                boxShadow: "0px 4px 12px 0px rgba(0,0,0,0.2)",
                minWidth: 240,
                mr: 1,
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: "16px",
                    py: "8px",
                    boxShadow: "0px 2px 3px rgba(0,0,0,0.1)",
                }}
            >
                <Typography
                    sx={{
                        fontSize: 14,
                        fontWeight: 600,
                        lineHeight: "20px",
                        letterSpacing: "0.15px",
                        fontFamily: "Inter, sans-serif",
                        color: TOOLBAR_COLORS.FLYOUT_TEXT,
                    }}
                >
                    {t("viewModifications.title")}
                </Typography>
                <Box
                    component="button"
                    type="button"
                    onClick={onResetAll}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        borderRadius: "4px",
                        backgroundColor: TOOLBAR_COLORS.FLYOUT_BUTTON_BG,
                        px: "8px",
                        py: "5px",
                        fontSize: 12,
                        fontWeight: 600,
                        lineHeight: "16px",
                        letterSpacing: "0.25px",
                        color: TOOLBAR_COLORS.FLYOUT_TEXT,
                        border: "none",
                        cursor: "pointer",
                        transition: "background-color 150ms",
                        "&:hover": { backgroundColor: TOOLBAR_COLORS.FLYOUT_BUTTON_BG_HOVER },
                        "&:active": { backgroundColor: TOOLBAR_COLORS.FLYOUT_BUTTON_BG_ACTIVE },
                    }}
                >
                    <ResetIcon style={{ width: 14, height: 14 }} aria-hidden="true" />
                    {t("viewModifications.reset")}
                </Box>
            </Box>

            {/* Token list */}
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                    p: "16px",
                }}
            >
                {categories.length === 0 ? (
                    <Typography
                        sx={{
                            py: 1,
                            textAlign: "center",
                            fontSize: 14,
                            fontWeight: 400,
                            lineHeight: "20px",
                            fontFamily: "Inter, sans-serif",
                            color: TOOLBAR_COLORS.FLYOUT_TEXT,
                        }}
                    >
                        {t("viewModifications.noActiveModifications")}
                    </Typography>
                ) : (
                    categories.map((category) => (
                        <FilterToken
                            key={category.id}
                            label={category.label}
                            count={category.count}
                            showCount={category.showCount}
                            isEnabled={category.isEnabled}
                            onToggle={category.onToggle}
                            onClear={category.onClear}
                            onActivate={category.onActivate}
                        />
                    ))
                )}
            </Box>
        </Box>
    );
}
